(() => {
  if (window.__hpProductCardBundleLoaded) return;
  window.__hpProductCardBundleLoaded = true;

  const MAX_CONCURRENT_PRELOADS = 8;
  const PRELOAD_MARGIN = '400px 0px';
  const SWIPE_THRESHOLD = 20;
  const SWIPE_MAX_MS = 800;
  const COLLECTION_LAZY_ROOT_MARGIN = '400px 0px';

  const isCollectionPage = () => document.body.hasAttribute('xo-collection');
  let collectionLazyObserver = null;
  let cardInitQueue = [];
  let cardInitScheduled = false;
  const CARD_INIT_BATCH = 3;

  function flushCardInitQueue() {
    cardInitScheduled = false;
    const batch = cardInitQueue.splice(0, CARD_INIT_BATCH);

    batch.forEach((card) => initProductCardFeatures(card));

    if (cardInitQueue.length) {
      cardInitScheduled = true;
      window.requestAnimationFrame(flushCardInitQueue);
    }
  }

  function queueCardInit(card) {
    if (!card || card.dataset.hpCardInitReady === 'true') return;
    cardInitQueue.push(card);

    if (!cardInitScheduled) {
      cardInitScheduled = true;
      window.requestAnimationFrame(flushCardInitQueue);
    }
  }

  function initProductCardFeatures(card) {
    if (!card || card.dataset.hpCardInitReady === 'true') return;
    card.dataset.hpCardInitReady = 'true';
    manager.init(card);
    colorManager.init(card);
    quickVariantManager.init(card);
    titleManager.init(card);
  }

  function ensureCollectionLazyObserver() {
    if (collectionLazyObserver || !('IntersectionObserver' in window)) return;

    collectionLazyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          manager.init(entry.target);
          queueCardInit(entry.target);
          collectionLazyObserver.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: COLLECTION_LAZY_ROOT_MARGIN, threshold: 0 }
    );
  }

  function queueCollectionCards(scope = document) {
    const cards = scope.matches?.('.xo-product-card')
      ? [scope]
      : Array.from(scope.querySelectorAll?.('.xo-product-card') || []);

    if (!cards.length) return false;

    ensureCollectionLazyObserver();

    if (collectionLazyObserver) {
      cards.forEach((card) => {
        if (card.dataset.hpCardInitReady === 'true') return;
        collectionLazyObserver.observe(card);
      });
      return true;
    }

    cards.forEach((card) => initProductCardFeatures(card));
    return true;
  }

  class ProductCardHoverManager {
    constructor() {
      this.preloadQueue = [];
      this.activePreloads = 0;
      this.observers = new WeakMap();
    }

    init(scope = document) {
      const root = scope.matches?.('[data-product-hover]') ? scope : scope;
      root.querySelectorAll?.('[data-product-hover]')?.forEach((container) => {
        if (container.dataset.productHoverReady === 'true') return;
        container.dataset.productHoverReady = 'true';
        this.setupContainer(container);
      });
    }

    destroy(scope = document) {
      const root = scope.matches?.('[data-product-hover]') ? scope : scope;
      root.querySelectorAll?.('[data-product-hover]')?.forEach((container) => {
        const observer = this.observers.get(container);
        observer?.disconnect();
        this.observers.delete(container);
        delete container.dataset.productHoverReady;
        delete container._hoverRaf;
        delete container._hoverPointer;
      });
    }

    setupContainer(container) {
      const images = this.getImages(container);
      if (images.length <= 1) return;

      const isTouch = window.matchMedia('(hover: none), (pointer: coarse)').matches;

      if (!isTouch) {
        container.addEventListener(
          'mouseenter',
          () => {
            this.preloadAllHoverImages(images);
          },
          { passive: true }
        );

        container.addEventListener('mousemove', (event) => {
          if (container.dataset.colorPreviewActive === 'true') return;

          container._hoverPointer = { clientX: event.clientX, clientY: event.clientY };
          if (container._hoverRaf) return;

          container._hoverRaf = window.requestAnimationFrame(() => {
            container._hoverRaf = null;
            const pointer = container._hoverPointer;
            if (!pointer) return;

            const index = this.getHoverIndexFromPointer(container, images.length, pointer);
            if (index == null) return;

            if (images[index]) this.queuePreload(images[index], true);

            const current = Number.parseInt(container.dataset.currentImage || '0', 10);
            if (index === current) return;

            this.showImageAtIndex(container, index, images);
          });
        });

        container.addEventListener('mouseleave', () => {
          if (container._hoverRaf) {
            window.cancelAnimationFrame(container._hoverRaf);
            container._hoverRaf = null;
          }

          container._hoverPointer = null;
          if (container.dataset.colorPreviewActive === 'true') return;
          this.resetContainer(container, images);
        });
      } else {
        this.setupSwipe(container, images);
        container.addEventListener(
          'touchstart',
          () => {
            images.slice(1).forEach((img) => this.queuePreload(img));
          },
          { passive: true }
        );
      }

      this.setupViewportPreload(container, images);
      this.revealImage(container, 0, images);
      this.preloadAllHoverImages(images);
    }

    getHoverIndexFromPointer(container, imageCount, event) {
      if (imageCount <= 1) return null;

      const rect = container.getBoundingClientRect();
      if (!rect.width) return null;

      const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 0.999999);
      return Math.min(imageCount - 1, Math.floor(ratio * imageCount));
    }

    getImages(container) {
      return Array.from(container.querySelectorAll('.product-hover-img, .lazy-hover-img')).sort(
        (a, b) =>
          Number.parseInt(a.dataset.imageIndex || '0', 10) - Number.parseInt(b.dataset.imageIndex || '0', 10)
      );
    }

    preloadAllHoverImages(images) {
      images.slice(1).forEach((img) => this.queuePreload(img));
    }

    setupViewportPreload(container, images) {
      if (!('IntersectionObserver' in window) || images.length <= 1) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.preloadAllHoverImages(images);
            observer.disconnect();
            this.observers.delete(container);
          });
        },
        { root: null, rootMargin: PRELOAD_MARGIN, threshold: 0 }
      );

      observer.observe(container);
      this.observers.set(container, observer);
    }

    setupSwipe(container, images) {
      let startX = 0;
      let startY = 0;
      let startTime = 0;

      container.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length !== 1) return;
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
          startTime = Date.now();
        },
        { passive: true }
      );

      container.addEventListener(
        'touchend',
        (event) => {
          if (event.changedTouches.length !== 1) return;

          // Inside a horizontally scrolling product row the swipe belongs to the row, not the card media.
          const scroller = container.closest('[data-hp-swipe-scroller]');
          if (scroller && scroller.scrollWidth - scroller.clientWidth > 1) return;

          const diffX = event.changedTouches[0].clientX - startX;
          const diffY = event.changedTouches[0].clientY - startY;
          const elapsed = Date.now() - startTime;

          if (elapsed > SWIPE_MAX_MS) return;
          if (Math.abs(diffX) < SWIPE_THRESHOLD) return;
          if (Math.abs(diffX) <= Math.abs(diffY)) return;

          const current = Number.parseInt(container.dataset.currentImage || '0', 10);
          const nextIndex = diffX < 0 ? current + 1 : current - 1;
          const clamped = Math.max(0, Math.min(images.length - 1, nextIndex));
          this.showImageAtIndex(container, clamped, images);
        },
        { passive: true }
      );
    }

    getTargetHoverWidth(container) {
      const rect = container?.getBoundingClientRect?.();
      const displayWidth = rect?.width || container?.clientWidth || 400;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      return Math.min(1000, Math.max(400, Math.ceil(displayWidth * dpr)));
    }

    parseSrcset(srcset) {
      if (!srcset) return [];

      return srcset
        .split(',')
        .map((part) => {
          const pieces = part.trim().split(/\s+/);
          const url = pieces[0];
          const width = Number.parseInt(pieces[1], 10);
          if (!url || Number.isNaN(width)) return null;
          return { url, width };
        })
        .filter(Boolean)
        .sort((a, b) => a.width - b.width);
    }

    pickSrcsetUrl(img, targetWidth) {
      const candidates = this.parseSrcset(img.dataset.srcset);
      if (!candidates.length) return img.dataset.src || '';

      return (
        candidates.find((candidate) => candidate.width >= targetWidth)?.url ||
        candidates[candidates.length - 1].url
      );
    }

    applyHoverImage(img, url) {
      img.src = url;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.classList.remove('lazy-hover-img');
    }

    queuePreload(img, priority = false) {
      if (!img?.classList.contains('lazy-hover-img') || img.dataset.loaded === 'true') return;

      const existingIndex = this.preloadQueue.indexOf(img);
      if (existingIndex !== -1) {
        if (priority && existingIndex > 0) {
          this.preloadQueue.splice(existingIndex, 1);
          this.preloadQueue.unshift(img);
        }
        return;
      }

      if (priority) {
        this.preloadQueue.unshift(img);
      } else {
        this.preloadQueue.push(img);
      }

      this.runPreloadQueue();
    }

    runPreloadQueue() {
      while (this.activePreloads < MAX_CONCURRENT_PRELOADS && this.preloadQueue.length > 0) {
        const img = this.preloadQueue.shift();
        this.activePreloads += 1;

        this.loadHoverImage(img).finally(() => {
          this.activePreloads -= 1;
          this.runPreloadQueue();
        });
      }
    }

    loadHoverImage(img) {
      return new Promise((resolve) => {
        const container = img.closest('[data-product-hover]');
        const targetWidth = this.getTargetHoverWidth(container);
        const previewWidth = Math.min(600, targetWidth);
        const previewUrl = this.pickSrcsetUrl(img, previewWidth);
        const finalUrl = this.pickSrcsetUrl(img, targetWidth);

        if (!previewUrl && !finalUrl) {
          resolve();
          return;
        }

        if (img.dataset.loaded === 'true') {
          resolve();
          return;
        }

        if (img.dataset.loaded === 'preview') {
          const fullLoader = new Image();
          fullLoader.onload = () => {
            this.applyHoverImage(img, finalUrl);
            img.dataset.loaded = 'true';
            resolve();
          };
          fullLoader.onerror = () => resolve();
          fullLoader.src = finalUrl;
          return;
        }

        const finish = (url, state) => {
          this.applyHoverImage(img, url);
          img.dataset.loaded = state;
          resolve();
        };

        if (previewUrl === finalUrl) {
          const loader = new Image();
          loader.onload = () => finish(finalUrl, 'true');
          loader.onerror = () => resolve();
          loader.src = finalUrl;
          return;
        }

        const previewLoader = new Image();
        previewLoader.onload = () => {
          img.src = previewUrl;
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
          img.dataset.loaded = 'preview';
          resolve();

          const fullLoader = new Image();
          fullLoader.onload = () => {
            this.applyHoverImage(img, finalUrl);
            img.dataset.loaded = 'true';
          };
          fullLoader.onerror = () => {};
          fullLoader.src = finalUrl;
        };
        previewLoader.onerror = () => resolve();
        previewLoader.src = previewUrl;
      });
    }

    preloadNeighbors(images, index) {
      [index - 1, index + 1].forEach((neighborIndex) => {
        if (images[neighborIndex]) this.queuePreload(images[neighborIndex]);
      });
    }

    showImageAtIndex(container, index, images) {
      const img = images[index];
      if (!img) return;

      if (
        img.classList.contains('lazy-hover-img') &&
        img.dataset.loaded !== 'true' &&
        img.dataset.loaded !== 'preview'
      ) {
        this.queuePreload(img, true);
      }

      const reveal = () => {
        this.revealImage(container, index, images);
        this.preloadNeighbors(images, index);
      };

      if (
        img.classList.contains('lazy-hover-img') &&
        img.dataset.loaded !== 'true' &&
        img.dataset.loaded !== 'preview'
      ) {
        this.loadHoverImage(img).then(reveal);
        return;
      }

      reveal();
    }

    revealImage(container, index, images) {
      images.forEach((imgEl, imgIndex) => {
        const isActive = imgIndex === index;
        imgEl.style.setProperty('opacity', isActive ? '1' : '0', 'important');
        imgEl.style.setProperty('z-index', isActive ? '2' : '1', 'important');
      });

      container.dataset.currentImage = String(index);
      this.updateDots(container, index);
    }

    resetContainer(container, images) {
      container.dataset.currentImage = '0';
      this.revealImage(container, 0, images);
    }

    updateDots(container, index) {
      container.querySelectorAll('.hover-dot').forEach((dot) => {
        const dotIndex = Number.parseInt(dot.dataset.dot, 10);
        dot.classList.toggle('active', dotIndex === index);
      });
    }

    previewVariantImage(card, imageUrl, imageSrcset = '') {
      if (!card || !imageUrl) return;

      const hoverContainer = card.querySelector('[data-product-hover]');
      if (hoverContainer) {
        const images = this.getImages(hoverContainer);
        const primary = images[0];
        if (!primary) return;

        if (!primary.dataset.originalSrc) {
          primary.dataset.originalSrc = primary.currentSrc || primary.src;
          primary.dataset.originalSrcset = primary.srcset || '';
        }

        hoverContainer.dataset.colorPreviewActive = 'true';
        primary.src = imageUrl;
        if (imageSrcset) {
          primary.srcset = imageSrcset;
        } else {
          primary.removeAttribute('srcset');
        }
        this.revealImage(hoverContainer, 0, images);
        return;
      }

      const primary =
        card.querySelector('.hp-product-media__image--primary') ||
        card.querySelector('.hp-product-media--single .hp-product-media__image') ||
        card.querySelector('.hp-product-media__image');
      if (!primary) return;

      if (!primary.dataset.originalSrc) {
        primary.dataset.originalSrc = primary.currentSrc || primary.src;
        primary.dataset.originalSrcset = primary.srcset || '';
      }

      card.dataset.colorPreviewActive = 'true';
      primary.src = imageUrl;
      if (imageSrcset) {
        primary.srcset = imageSrcset;
      } else {
        primary.removeAttribute('srcset');
      }
    }

    resetVariantPreview(card) {
      if (!card) return;

      const hoverContainer = card.querySelector('[data-product-hover]');
      if (hoverContainer) {
        const images = this.getImages(hoverContainer);
        const primary = images[0];

        if (primary?.dataset.originalSrc) {
          primary.src = primary.dataset.originalSrc;
          if (primary.dataset.originalSrcset) {
            primary.srcset = primary.dataset.originalSrcset;
          } else {
            primary.removeAttribute('srcset');
          }
          delete primary.dataset.originalSrc;
          delete primary.dataset.originalSrcset;
        }

        delete hoverContainer.dataset.colorPreviewActive;
        this.resetContainer(hoverContainer, images);
        return;
      }

      const primary =
        card.querySelector('.hp-product-media__image--primary') ||
        card.querySelector('.hp-product-media--single .hp-product-media__image') ||
        card.querySelector('.hp-product-media__image');
      if (!primary?.dataset.originalSrc) {
        delete card.dataset.colorPreviewActive;
        return;
      }

      primary.src = primary.dataset.originalSrc;
      if (primary.dataset.originalSrcset) {
        primary.srcset = primary.dataset.originalSrcset;
      } else {
        primary.removeAttribute('srcset');
      }

      delete primary.dataset.originalSrc;
      delete primary.dataset.originalSrcset;
      delete card.dataset.colorPreviewActive;
    }
  }

  class ProductCardColors {
    constructor(hoverManager) {
      this.hoverManager = hoverManager;
      this.nextListId = 0;
      this.controllers = new WeakMap();
    }

    init(scope = document) {
      const cards = scope.matches?.('.xo-product-card')
        ? [scope]
        : Array.from(scope.querySelectorAll?.('.xo-product-card') || []);

      cards.forEach((card) => {
        if (card.dataset.colorSwatchesReady === 'true') return;

        const swatches = card.querySelectorAll('[data-variant-preview-image]');
        const siblings = card.querySelector('[data-color-siblings]');
        if (!swatches.length && !siblings) return;

        card.dataset.colorSwatchesReady = 'true';
        const controller = new window.AbortController();
        this.controllers.set(card, controller);
        const listenerOptions = { signal: controller.signal };

        swatches.forEach((swatch) => {
          swatch.addEventListener('mouseenter', () => {
            const imageUrl = swatch.dataset.variantPreviewImage;
            if (!imageUrl) return;
            this.hoverManager.previewVariantImage(card, imageUrl);
          }, listenerOptions);

          swatch.addEventListener('mouseleave', () => {
            this.hoverManager.resetVariantPreview(card);
          }, listenerOptions);
        });

        if (siblings) {
          const toggle = siblings.querySelector('[data-color-siblings-toggle]');
          const extra = siblings.querySelector('[data-color-siblings-extra]');
          if (toggle && extra) {
            extra.id = `hp-card-colors-${++this.nextListId}`;
            toggle.setAttribute('aria-controls', extra.id);
            toggle.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();
              extra.hidden = !extra.hidden;
              toggle.setAttribute('aria-expanded', String(!extra.hidden));
            }, listenerOptions);
          }

          const chips = siblings.querySelectorAll('button.hp-product-card__color-chip');
          chips.forEach((chip) => {
            chip.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();
              if (!chip.dataset.cardImage) return;
              chips.forEach((candidate) => candidate.setAttribute('aria-pressed', String(candidate === chip)));
              this.hoverManager.previewVariantImage(card, chip.dataset.cardImage, chip.dataset.cardImageSrcset || '');
              if (chip.dataset.cardUrl) {
                card.querySelectorAll('.xo-card__media a, .hp-product-card__media-wrap a').forEach(link => {
                  if (link.querySelector('.hp-product-media')) link.setAttribute('href', chip.dataset.cardUrl);
                });
              }
              const productRoot = card.closest('xo-product');
              if (productRoot) productRoot.dataset.hpProductAvailable = chip.dataset.cardAvailable;
            }, listenerOptions);
          });

          siblings.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape' || !extra || extra.hidden) return;
            extra.hidden = true;
            toggle?.setAttribute('aria-expanded', 'false');
            toggle?.focus();
          }, listenerOptions);
        }
      });
    }

    destroy(scope = document) {
      const cards = scope.matches?.('.xo-product-card[data-color-swatches-ready="true"]')
        ? [scope]
        : Array.from(scope.querySelectorAll?.('.xo-product-card[data-color-swatches-ready="true"]') || []);

      cards.forEach((card) => {
        this.controllers.get(card)?.abort();
        this.controllers.delete(card);
        delete card.dataset.colorSwatchesReady;
        delete card.dataset.colorPreviewActive;
      });
    }
  }

  class ProductCardQuickVariant {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('[data-quick-variant-panel]');
      this.toggle = root.querySelector('[data-quick-variant-toggle]');
      this.closeBtn = root.querySelector('[data-quick-variant-close]');
      this.cartAdd = root.querySelector('xo-cart-add');
      this.sizeList = root.querySelector('[data-size-list]');
      this.viewport = root.querySelector('[data-size-viewport]');
      this.prevBtn = root.querySelector('[data-size-prev]');
      this.nextBtn = root.querySelector('[data-size-next]');
      this.data = JSON.parse(root.querySelector('[data-product-variants]')?.textContent || '{}');
      this.sizeIndex = Number.parseInt(root.dataset.sizeOptionIndex, 10);
      this.scrollPosition = 0;
      this.step = 0;
      this.isOpen = false;
    }

    init() {
      this.toggle?.addEventListener('click', (event) => this.onToggle(event));
      this.closeBtn?.addEventListener('click', (event) => this.onClose(event));
      this.root.querySelectorAll('[data-size-value]').forEach((button) => {
        button.addEventListener('click', (event) => this.onSizeClick(event));
      });
      this.prevBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.scrollSizes(-1);
      });
      this.nextBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.scrollSizes(1);
      });
      this.measureStep();
    }

    getSelectedOptions() {
      const selected = this.data.variants?.find(
        (variant) => Number(variant.id) === Number(this.data.selectedVariantId)
      );
      return selected?.options || [];
    }

    findVariantForSize(sizeValue) {
      const selectedOptions = this.getSelectedOptions();
      return this.data.variants?.find((variant) => {
        if (!variant.available) return false;
        if (variant.options?.[this.sizeIndex] !== sizeValue) return false;
        return variant.options.every(
          (option, index) => index === this.sizeIndex || option === selectedOptions[index]
        );
      });
    }

    onToggle(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.isOpen) {
        this.setOpen(false);
        return;
      }

      ProductCardQuickVariantManager.closeAll(this.root);
      this.setOpen(true);
    }

    onClose(event) {
      event.preventDefault();
      event.stopPropagation();
      this.setOpen(false);
    }

    setOpen(open) {
      this.isOpen = open;
      this.root.classList.toggle('is-open', open);
      if (this.panel) this.panel.hidden = !open;
      this.toggle?.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.toggle?.setAttribute('aria-hidden', open ? 'true' : 'false');
      this.closeBtn?.setAttribute('aria-hidden', open ? 'false' : 'true');
      this.closeBtn?.setAttribute('tabindex', open ? '0' : '-1');

      if (open) {
        this.resetScroll();
        window.requestAnimationFrame(() => {
          this.measureStep();
          this.updateNavState();
        });
      }
    }

    onSizeClick(event) {
      const button = event.currentTarget;
      if (button.classList.contains('is-disabled') || button.disabled) return;

      event.preventDefault();
      event.stopPropagation();

      const variant = this.findVariantForSize(button.dataset.sizeValue);
      if (!variant || !this.cartAdd) return;

      this.root.classList.add('is-adding');
      button.setAttribute('aria-busy', 'true');
      this.triggerAddToCart(variant.id);

      const finish = () => {
        this.root.classList.remove('is-adding');
        button.removeAttribute('aria-busy');
        this.setOpen(false);
      };

      const observer = new MutationObserver(() => {
        if (this.cartAdd.getAttribute('xo-loading') === 'true') return;
        observer.disconnect();
        finish();
      });

      observer.observe(this.cartAdd, { attributes: true, attributeFilter: ['xo-loading'] });

      window.setTimeout(() => {
        observer.disconnect();
        if (this.isOpen) finish();
      }, 4000);
    }

    triggerAddToCart(variantId) {
      if (!this.cartAdd) return;

      this.cartAdd.setAttribute('xo-variant-id', String(variantId));

      if (this.data.productId) {
        this.cartAdd.setAttribute('xo-product-id', String(this.data.productId));
      }

      const cartButton = this.cartAdd.querySelector('button');
      if (cartButton) {
        cartButton.click();
        return;
      }

      this.cartAdd.click();
    }

    measureStep() {
      const first = this.sizeList?.querySelector('.hp-product-card__quick-variant-button');
      if (!first) return;

      const second = first.nextElementSibling;
      if (second) {
        const step = second.offsetTop - first.offsetTop;
        if (step > 0) {
          this.step = step;
          return;
        }
      }

      const height = first.offsetHeight || first.getBoundingClientRect().height;
      if (height > 0) {
        this.step = height;
        return;
      }

      const rowSize = getComputedStyle(this.root).getPropertyValue('--hp-quick-size-row').trim();
      this.step = Number.parseFloat(rowSize) || 36;
    }

    getMaxScroll() {
      if (!this.sizeList || !this.viewport) return 0;
      return Math.max(0, this.sizeList.scrollHeight - this.viewport.clientHeight);
    }

    resetScroll() {
      this.scrollPosition = 0;
      if (this.sizeList) this.sizeList.style.transform = 'translate3d(0, 0, 0)';
      this.updateNavState();
    }

    updateNavState() {
      if (!this.prevBtn && !this.nextBtn) return;

      const maxScroll = this.getMaxScroll();
      if (this.prevBtn) this.prevBtn.disabled = this.scrollPosition <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.scrollPosition >= maxScroll;
    }

    scrollSizes(direction) {
      if (!this.sizeList || !this.viewport) return;
      if (!this.step) this.measureStep();

      const step = this.step || 36;
      const maxScroll = this.getMaxScroll();
      this.scrollPosition = Math.max(0, Math.min(maxScroll, this.scrollPosition + direction * step));
      this.sizeList.style.transform = `translate3d(0, -${this.scrollPosition}px, 0)`;
      this.updateNavState();
    }
  }

  class ProductCardQuickVariantManager {
    static instances = new Set();

    static register(instance) {
      ProductCardQuickVariantManager.instances.add(instance);
    }

    static unregister(instance) {
      ProductCardQuickVariantManager.instances.delete(instance);
    }

    static closeAll(exceptRoot = null) {
      ProductCardQuickVariantManager.instances.forEach((instance) => {
        if (exceptRoot && instance.root === exceptRoot) return;
        if (instance.isOpen) instance.setOpen(false);
      });
    }

    constructor() {
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.documentBound = false;
    }

    init(scope = document) {
      ProductCardQuickVariantManager.instances.forEach((instance) => {
        if (instance.root.isConnected) return;
        instance.setOpen(false);
        ProductCardQuickVariantManager.unregister(instance);
      });

      const roots = scope.matches?.('[data-product-card-quick-variant]')
        ? [scope]
        : Array.from(scope.querySelectorAll?.('[data-product-card-quick-variant]') || []);

      roots.forEach((root) => {
        if (root.__hpQuickVariant) return;
        root.dataset.quickVariantReady = 'true';
        const instance = new ProductCardQuickVariant(root);
        instance.init();
        ProductCardQuickVariantManager.register(instance);
        root.__hpQuickVariant = instance;
      });

      if (!this.documentBound) {
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
        this.documentBound = true;
      }
    }

    onDocumentClick(event) {
      if (event.target.closest('[data-product-card-quick-variant]')) return;
      ProductCardQuickVariantManager.closeAll();
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') ProductCardQuickVariantManager.closeAll();
    }

    destroy(scope = document) {
      scope.querySelectorAll('[data-product-card-quick-variant]')?.forEach((root) => {
        const instance = root.__hpQuickVariant;
        if (instance) {
          instance.setOpen(false);
          ProductCardQuickVariantManager.unregister(instance);
          delete root.__hpQuickVariant;
        }
        delete root.dataset.quickVariantReady;
        root.classList.remove('is-open', 'is-adding');
      });
    }
  }

  class ProductCardTitles {
    constructor() {
      this.titles = new Set();
      this.widths = new WeakMap();
      this.pending = new Set();
      this.scheduled = false;
      this.nextId = 0;
      this.observer = 'ResizeObserver' in window
        ? new window.ResizeObserver((entries) => this.onResize(entries))
        : null;

      document.addEventListener('click', (event) => this.onClick(event), true);
      if (!this.observer) window.addEventListener('resize', () => this.refresh(), { passive: true });
      document.fonts?.ready?.then(() => this.refresh());
      document.fonts?.addEventListener?.('loadingdone', () => this.refresh());
    }

    init(scope = document) {
      scope.querySelectorAll?.('[data-card-title]')?.forEach((title) => {
        if (this.titles.has(title)) return;

        const text = title.querySelector('.xo-text');
        const toggle = title.querySelector('[data-card-title-toggle]');
        if (!text || !toggle) return;

        if (!text.id) text.id = `hp-card-title-${++this.nextId}`;
        toggle.setAttribute('aria-controls', text.id);
        this.titles.add(title);

        if (this.observer) this.observer.observe(title);
        else this.queue(title);
      });
    }

    destroy(scope = document) {
      this.titles.forEach((title) => {
        if (title.isConnected && !scope.contains?.(title)) return;
        this.forget(title);
      });
    }

    forget(title) {
      this.observer?.unobserve(title);
      this.titles.delete(title);
      this.widths.delete(title);
      this.pending.delete(title);
    }

    refresh() {
      this.titles.forEach((title) => this.queue(title));
    }

    queue(title) {
      this.pending.add(title);
      if (this.scheduled) return;

      this.scheduled = true;
      window.requestAnimationFrame(() => this.flush());
    }

    onResize(entries) {
      entries.forEach((entry) => {
        const width = Math.round(entry.contentRect.width);
        if (this.widths.get(entry.target) === width) return;
        this.widths.set(entry.target, width);
        this.queue(entry.target);
      });
    }

    onClick(event) {
      const toggle = event.target.closest?.('[data-card-title-toggle]');
      if (!toggle) return;

      event.preventDefault();
      event.stopPropagation();

      const title = toggle.closest('[data-card-title]');
      if (!title) return;
      this.setExpanded(title, toggle, !title.classList.contains('is-expanded'));
    }

    setExpanded(title, toggle, expanded) {
      title.classList.toggle('is-expanded', expanded);
      toggle.setAttribute('aria-expanded', String(expanded));

      const label = expanded ? toggle.dataset.labelCollapse : toggle.dataset.labelExpand;
      if (label) toggle.setAttribute('aria-label', label);
    }

    firstLineWidth(text) {
      const walker = document.createTreeWalker(text, window.NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();
      while (node && !node.data.trim()) node = walker.nextNode();
      if (!node) return 0;

      const range = document.createRange();
      range.selectNodeContents(node);
      const rects = Array.from(range.getClientRects()).filter((rect) => rect.width > 0);
      if (!rects.length) return 0;

      const top = rects[0].top;
      const right = Math.max(...rects.filter((rect) => Math.abs(rect.top - top) < 2).map((rect) => rect.right));
      return Math.max(0, Math.ceil(right - text.getBoundingClientRect().left));
    }

    flush() {
      this.scheduled = false;

      const batch = [];
      this.pending.forEach((title) => {
        if (!title.isConnected) {
          this.forget(title);
          return;
        }

        const text = title.querySelector('.xo-text');
        const toggle = title.querySelector('[data-card-title-toggle]');
        if (!text || !toggle || !title.clientWidth) return;
        batch.push({ title, text, toggle, expanded: title.classList.contains('is-expanded') });
      });
      this.pending.clear();
      if (!batch.length) return;

      batch.forEach(({ title, toggle }) => {
        title.classList.add('is-ready');
        title.classList.remove('is-truncated', 'is-expanded');
        title.style.removeProperty('--hp-card-title-fit');
        toggle.hidden = true;
      });

      batch.forEach((item) => {
        // The title block can carry its own padding (e.g. PDP recommendations): the
        // one-line max-height is border-box, so padding must not count as overflow.
        const style = window.getComputedStyle(item.text);
        const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
        item.truncated = item.text.scrollHeight - padding > item.text.clientHeight + 1;
      });

      batch.forEach(({ title, toggle, truncated, expanded }) => {
        title.classList.toggle('is-truncated', truncated);
        toggle.hidden = !truncated;
        this.setExpanded(title, toggle, truncated && expanded);
      });

      batch.forEach((item) => {
        if (!item.truncated) return;
        item.fit = this.firstLineWidth(item.text);
        item.color = window.getComputedStyle(item.text).color;
      });

      batch.forEach(({ title, toggle, truncated, fit, color }) => {
        if (!truncated) return;
        if (fit) title.style.setProperty('--hp-card-title-fit', `${fit}px`);
        toggle.style.color = color;
      });
    }
  }

  class ProductCardCampaignBadge extends HTMLElement {
    connectedCallback() {
      this.texts = Array.from(this.querySelectorAll('.hp-product-card__campaign-badge-text'));
      this.currentIndex = this.texts.findIndex((element) => element.classList.contains('is-active'));
      if (this.currentIndex < 0) {
        this.currentIndex = 0;
        this.texts[0]?.classList.add('is-active');
      }

      if (this.texts.length <= 1) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.intervalMs = Number.parseInt(this.dataset.interval || '5000', 10) || 5000;
      this.timer = window.setInterval(() => this.rotate(), this.intervalMs);
    }

    disconnectedCallback() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    rotate() {
      this.texts[this.currentIndex]?.classList.remove('is-active');
      this.currentIndex = (this.currentIndex + 1) % this.texts.length;
      this.texts[this.currentIndex]?.classList.add('is-active');
    }
  }

  if (!customElements.get('product-card-campaign-badge')) {
    customElements.define('product-card-campaign-badge', ProductCardCampaignBadge);
  }

  const manager = new ProductCardHoverManager();
  const colorManager = new ProductCardColors(manager);
  const quickVariantManager = new ProductCardQuickVariantManager();
  const titleManager = new ProductCardTitles();

  let dynamicInitScheduled = false;
  const dynamicInitTargets = new Set();

  function queueDynamicInit(target) {
    if (target) dynamicInitTargets.add(target);
    if (dynamicInitScheduled) return;

    dynamicInitScheduled = true;
    window.requestAnimationFrame(() => {
      dynamicInitScheduled = false;
      const targets = [...dynamicInitTargets];
      dynamicInitTargets.clear();
      targets.forEach((scope) => scheduleInit(scope));
    });
  }

  function observeDynamicProductSections(scope = document) {
    const selector = 'xo-product-recommendations, xo-products-fetcher, xo-cart-mini';
    const roots = scope.matches?.(selector)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(selector) || []);

    roots.forEach((root) => {
      if (root.dataset.hpDynamicCardsObserved === 'true') return;
      root.dataset.hpDynamicCardsObserved = 'true';

      const observer = new MutationObserver((mutations) => {
        const hasAddedNodes = mutations.some((mutation) => mutation.addedNodes.length > 0);
        if (!hasAddedNodes) return;
        queueDynamicInit(root);
      });

      observer.observe(root, { childList: true, subtree: true });
    });
  }

  function scheduleInit(scope = document) {
    const run = () => {
      if (!scope.isConnected) return;
      scope.querySelectorAll?.('.cart-drawer__recommendations').forEach((section) => {
        quickVariantManager.init(section);
      });
      if (isCollectionPage() && queueCollectionCards(scope)) return;

      manager.init(scope);
      colorManager.init(scope);
      quickVariantManager.init(scope);
      titleManager.init(scope);
    };

    window.requestAnimationFrame(run);
  }

  function scheduleDestroy(scope = document) {
    scope.querySelectorAll?.('.xo-product-card')?.forEach((card) => {
      collectionLazyObserver?.unobserve(card);
      delete card.dataset.hpCardInitReady;
    });

    titleManager.destroy(scope);
    quickVariantManager.destroy(scope);
    colorManager.destroy(scope);
    manager.destroy(scope);
  }

  const initialize = () => {
    observeDynamicProductSections();
    scheduleInit();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => {
    scheduleDestroy(event.target);
    observeDynamicProductSections(event.target);
    scheduleInit(event.target);
  });

  window.ProductCardHoverManager = manager;
  window.initProductCardHover = (scope) => scheduleInit(scope);
})();
